import type { PropertyDefinition } from "@/hooks/useConnectorPropertyDefinitions";

/**
 * Registry-driven target fields on the connector CREATE form.
 *
 * The create form has no connector id yet, so it cannot use the connector-scoped
 * property-definitions endpoint that knows about target-side settings. It reads
 * `?role=target` on the type-scoped one instead. These helpers turn that list
 * into the three things the form needs — what to render, what to complain
 * about, and what to POST — and live outside the component so each is testable
 * on its own.
 *
 * Required-only throughout. Optional target settings (the Xero invoice flags,
 * the FTP timeouts) already render on the EDIT sheet; surfacing them at create
 * is a product call, not part of fixing the dead end.
 */

/** The definitions the create form renders and enforces. */
export function requiredTargetDefs(
  definitions: PropertyDefinition[],
): PropertyDefinition[] {
  return definitions.filter((d) => d.required);
}

/**
 * The first required definition the user has not filled in, or null.
 *
 * Whitespace does not count as filled: the backend trims before its own
 * required check, so accepting "   " here would produce a 422 the form had
 * already declared valid.
 */
export function missingRequiredTargetDef(
  definitions: PropertyDefinition[],
  values: Record<string, string>,
): PropertyDefinition | null {
  return (
    requiredTargetDefs(definitions).find(
      (d) => !(values[d.name] ?? "").trim(),
    ) ?? null
  );
}

/**
 * The properties to merge into the create POST body.
 *
 * Keyed by the definition's own `name`, never a retyped literal. The backend's
 * encryption rules are matched with `re.match` and NO IGNORECASE, so
 * `asana.personalaccesstoken` misses the rule that encrypts
 * `asana.personalAccessToken` and the token would be stored in clear. Taking
 * the key off the definition is what makes that miscasing impossible here.
 *
 * Values are trimmed, for the same reason `missingRequiredTargetDef` trims: a
 * credential with a stray leading space fails against the third-party API in a
 * way that looks like a bad token rather than a bad paste.
 */
export function targetPropertiesPayload(
  definitions: PropertyDefinition[],
  values: Record<string, string>,
): Record<string, string> {
  const payload: Record<string, string> = {};
  for (const def of requiredTargetDefs(definitions)) {
    const value = (values[def.name] ?? "").trim();
    if (value) payload[def.name] = value;
  }
  return payload;
}
